import React from 'react';
import { formatAmount } from '../../datasets/utils/formatters';

interface FeeStructure {
  approved: {
    fixedFee: number;
    percentageFee: number;
    maxCap: number;
  };
  declined: {
    fixedFee: number;
    percentageFee: number;
    maxCap: number;
  };
  returned: {
    fixedFee: number;
    percentageFee: number;
    maxCap: number;
  };
}

interface FeeComparisonTableProps {
  title: string;
  quote1: {
    name: string;
    fees: FeeStructure;
    bankFees?: FeeStructure;
  };
  quote2: {
    name: string;
    fees: FeeStructure;
    bankFees?: FeeStructure;
  };
}

export function FeeComparisonTable({ title, quote1, quote2 }: FeeComparisonTableProps) {
  const calculateDifference = (value1: number, value2: number) => {
    const diff = value2 - value1;
    const isPositive = diff > 0;
    return {
      value: Math.abs(diff),
      isPositive,
      text: isPositive ? 'higher' : 'lower' 
    };
  };

  const renderFeeDifference = (fee1: number, fee2: number, type: 'amount' | 'percentage') => {
    const { value, isPositive, text } = calculateDifference(fee1, fee2);
    if (value === 0) return null;
    
    return (
      <span className={`text-xs ${isPositive ? 'text-red-600' : 'text-green-600'}`}>
        ({type === 'amount' ? formatAmount(value) : `${value.toFixed(3)}%`} {text})
      </span>
    );
  };
  
  const renderMargin = (merchantFee: number, bankFee: number, type: 'amount' | 'percentage') => {
    const margin = type === 'amount' ? merchantFee - bankFee : Number((merchantFee - bankFee).toFixed(3));
    return (
      <span className={`text-xs ${margin >= 0 ? 'text-green-600' : 'text-red-600'}`}>
        (Margin: {type === 'amount' ? formatAmount(margin) : `${margin}%`})
      </span>
    );
  };

  return (
    <div className="bg-white border rounded-lg overflow-hidden">
      <div className="px-4 py-3 bg-gray-50 border-b">
        <h3 className="text-lg font-medium text-gray-900">{title}</h3>
        <div className="grid grid-cols-2 gap-8">
          <div className="text-sm font-medium text-gray-900">{quote1.name}</div>
          <div className="text-sm font-medium text-gray-900 flex justify-between items-center">
            <span>{quote2.name}</span>
            <span className="text-xs text-gray-500">vs {quote1.name}</span>
          </div>
        </div>
      </div>
      <div className="p-6">
        <div className="grid grid-cols-2 gap-8">
          {/* Quote 1 */}
          <div className="grid grid-rows-3 gap-6">
            {(['approved', 'declined', 'returned'] as const).map((status) => (
              <div key={`${status}-1`} className="bg-gray-50 rounded-lg p-4 h-full">
                <h4 className="text-sm font-medium text-gray-900 capitalize mb-4">{status}</h4>
                <div className="space-y-4">
                  <div className="flex justify-between items-baseline">
                    <span className="text-sm text-gray-600">Fixed Fee</span>
                    <div className="text-right">
                      <span className="text-sm font-medium text-gray-900">
                        {formatAmount(quote1.fees[status].fixedFee)}
                      </span>
                      {quote1.bankFees && (
                        <div className="mt-1 flex items-center justify-end space-x-2">
                          <span className="text-xs text-gray-500">
                            Bank: {formatAmount(quote1.bankFees[status].fixedFee)}
                          </span>
                          {renderMargin(quote1.fees[status].fixedFee, quote1.bankFees[status].fixedFee, 'amount')}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex justify-between items-baseline">
                    <span className="text-sm text-gray-600">Rate Fee</span>
                    <div className="text-right">
                      <span className="text-sm font-medium text-gray-900">
                        {quote1.fees[status].percentageFee}%
                      </span>
                      {quote1.bankFees && (
                        <div className="mt-1 flex items-center justify-end space-x-2">
                          <span className="text-xs text-gray-500">
                            Bank: {quote1.bankFees[status].percentageFee}%
                          </span>
                          {renderMargin(quote1.fees[status].percentageFee, quote1.bankFees[status].percentageFee, 'percentage')}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex justify-between items-baseline">
                    <span className="text-sm text-gray-600">Max Cap</span>
                    <div className="text-right">
                      <span className="text-sm font-medium text-gray-900">
                        {formatAmount(quote1.fees[status].maxCap)}
                      </span>
                      {quote1.bankFees && (
                        <div className="mt-1 flex items-center justify-end space-x-2">
                          <span className="text-xs text-gray-500">
                            Bank: {formatAmount(quote1.bankFees[status].maxCap)}
                          </span>
                          {renderMargin(quote1.fees[status].maxCap, quote1.bankFees[status].maxCap, 'amount')}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Quote 2 */}
          <div className="grid grid-rows-3 gap-6">
            {(['approved', 'declined', 'returned'] as const).map((status) => (
              <div key={`${status}-2`} className="bg-gray-50 rounded-lg p-4 h-full">
                <h4 className="text-sm font-medium text-gray-900 capitalize mb-4">{status}</h4>
                <div className="space-y-4">
                  <div className="flex justify-between items-baseline">
                    <span className="text-sm text-gray-600">Fixed Fee</span>
                    <div className="text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <span className="text-sm font-medium text-gray-900">
                          {formatAmount(quote2.fees[status].fixedFee)} 
                        </span>
                        {renderFeeDifference(quote1.fees[status].fixedFee, quote2.fees[status].fixedFee, 'amount')}
                      </div>
                      {quote2.bankFees && (
                        <div className="mt-1 flex items-center justify-end space-x-2">
                          <span className="text-xs text-gray-500">
                            Bank: {formatAmount(quote2.bankFees[status].fixedFee)}
                          </span>
                          {renderMargin(quote2.fees[status].fixedFee, quote2.bankFees[status].fixedFee, 'amount')}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex justify-between items-baseline">
                    <span className="text-sm text-gray-600">Rate Fee</span>
                    <div className="text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <span className="text-sm font-medium text-gray-900">
                          {quote2.fees[status].percentageFee}% 
                        </span>
                        {renderFeeDifference(quote1.fees[status].percentageFee, quote2.fees[status].percentageFee, 'percentage')}
                      </div>
                      {quote2.bankFees && (
                        <div className="mt-1 flex items-center justify-end space-x-2">
                          <span className="text-xs text-gray-500">
                            Bank: {quote2.bankFees[status].percentageFee}%
                          </span>
                          {renderMargin(quote2.fees[status].percentageFee, quote2.bankFees[status].percentageFee, 'percentage')}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex justify-between items-baseline">
                    <span className="text-sm text-gray-600">Max Cap</span>
                    <div className="text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <span className="text-sm font-medium text-gray-900">
                          {formatAmount(quote2.fees[status].maxCap)} 
                        </span>
                        {renderFeeDifference(quote1.fees[status].maxCap, quote2.fees[status].maxCap, 'amount')}
                      </div>
                      {quote2.bankFees && (
                        <div className="mt-1 flex items-center justify-end space-x-2">
                          <span className="text-xs text-gray-500">
                            Bank: {formatAmount(quote2.bankFees[status].maxCap)}
                          </span>
                          {renderMargin(quote2.fees[status].maxCap, quote2.bankFees[status].maxCap, 'amount')}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}